/** A stage from its title card to its last enemy, and on to the next (docs/spec/02 §7, §9; 03 §1, §5). */

import { newDirector } from './director';
import { startEmpress } from './empress';
import { newFormation } from './formation';
import { addScore } from './score';
import { loadSpawner, spawnerDone } from './spawn';
import { challengeWaves, formationWaves, stageKind } from './stages';
import { CHALLENGE, difficulty, FLOW, heat } from './tuning';
import type { GameState } from './types';

/** Set the board for `stage` and show its title card. */
export function beginStage(s: GameState, stage: number): void {
  const first = s.tick === 0;
  s.stage = stage;
  s.stageKind = stageKind(s.mode, stage);
  s.phase = 'intro';
  s.phaseT = 0;
  s.enemies = [];
  s.bombs = [];
  s.beam = null;
  s.empress = null;
  s.challenge = null;
  Object.assign(s.formation, newFormation());
  const keep = s.director;
  Object.assign(keep, newDirector(), { nextGroup: keep.nextGroup, nextTrio: keep.nextTrio });
  // The first dive comes a beat after the formation settles.
  keep.timer = 50;
  if (s.stageKind === 'formation') loadSpawner(s.spawner, formationWaves(stage, difficulty(heat(s.mode, stage))));
  else if (s.stageKind === 'challenge') {
    s.challenges++;
    s.challenge = { index: s.challenges, hits: 0, waveHits: [0, 0, 0, 0, 0], bonus: 0, perfect: false };
    loadSpawner(s.spawner, challengeWaves(s.challenges));
  } else loadSpawner(s.spawner, []);
  s.events.push({ type: 'stageStart', stage, kind: s.stageKind });
  if (s.stageKind === 'empress') s.events.push({ type: 'warning' });
  // The opening card is longer: it has the start jingle to get through.
  if (first && s.stageKind === 'formation') s.phaseT = FLOW.intro - FLOW.firstIntro;
}

function introTicks(s: GameState): number {
  if (s.stageKind === 'challenge') return FLOW.challengeIntro;
  if (s.stageKind === 'empress') return FLOW.warning;
  return FLOW.intro;
}

/** The challenging stage's reckoning (02 §7). */
function tally(s: GameState): void {
  const c = s.challenge;
  s.phase = 'tally';
  s.phaseT = 0;
  if (!c) return;
  c.perfect = c.hits >= 40;
  c.bonus = c.perfect ? CHALLENGE.perfect : c.hits * CHALLENGE.hit;
  addScore(s, c.bonus);
  s.events.push({ type: 'tally', hits: c.hits, bonus: c.bonus, perfect: c.perfect });
}

function clear(s: GameState): void {
  s.phase = 'clear';
  s.phaseT = 0;
  s.bombs = [];
  s.events.push({ type: 'stageClear', stage: s.stage });
}

/** The last fighter is gone and none is coming: the game is about to be over. */
function doomed(s: GameState): boolean {
  return s.player.status === 'dead' && s.lives === 0 && !s.rescue;
}

export function updateFlow(s: GameState): void {
  s.phaseT++;
  switch (s.phase) {
    case 'intro':
      if (s.phaseT < introTicks(s)) return;
      s.phaseT = 0;
      if (s.stageKind === 'formation') s.phase = 'entering';
      else if (s.stageKind === 'challenge') s.phase = 'challenge';
      else {
        s.phase = 'empress';
        startEmpress(s);
      }
      return;
    case 'battle':
      // A rescue in the air is let land before the card changes.
      if (s.enemies.length === 0 && !s.rescue) clear(s);
      return;
    case 'challenge':
      if (spawnerDone(s) && s.enemies.length === 0) tally(s);
      return;
    case 'empress':
      if (!s.empress && s.enemies.length === 0) clear(s);
      return;
    // A game lost with the stage's last enemy ends on that stage, not on the title card of the next.
    case 'tally':
      if (s.phaseT >= CHALLENGE.tallyTicks && !doomed(s)) beginStage(s, s.stage + 1);
      return;
    case 'clear':
      if (s.phaseT >= FLOW.clear && !doomed(s)) beginStage(s, s.stage + 1);
      return;
    default:
  }
}
